import React, { useEffect, useState, useMemo } from "react";
import { observer } from "mobx-react-lite";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  MenuItem,
  Select,
  Card,
  CardContent,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import roleStore from "../../stores/RoleStore";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AddIcon from "@mui/icons-material/Add";
import CustomDataGridPro from "../CustomDataGridPro";

const RoleComponent = observer(() => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRoleId, setCurrentRoleId] = useState(null);
  const [roleData, setRoleData] = useState({ name: "", description: "" });
  const [selectedPermission, setSelectedPermission] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    roleStore.fetchRoles();
    roleStore.fetchAvailablePermissions();
  }, []);

  const handleOpenModal = (role = null) => {
    if (role) {
      setRoleData(role);
      setEditMode(true);
      setCurrentRoleId(role.id);
      roleStore.fetchRolePermissions(role.id);
    } else {
      setRoleData({ name: "", description: "" });
      setEditMode(false);
    }
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!roleData.name || !roleData.description) {
      alert("Please enter role name and description.");
      return;
    }

    if (editMode) {
      await roleStore.updateRole(currentRoleId, roleData);
    } else {
      await roleStore.createRole(roleData);
    }

    setIsModalOpen(false);
  };

  // const columns = [
  //   {
  //     field: "id",
  //     headerName: "ID",
  //     flex: 1,
  //   },
  //   {
  //     field: "name",
  //     headerName: "Role Name",
  //     flex: 1,
  //   },
  //   {
  //     field: "description",
  //     headerName: "Description",
  //     flex: 1,
  //   },
  //   {
  //     field: "permissions",
  //     headerName: "Permissions",
  //     flex: 2,
  //     renderCell: (params) => (
  //       <Box>
  //         {params.value?.map((perm) => (
  //           <Box key={perm.id} display="flex" alignItems="center">
  //             {perm.name}
  //             <IconButton
  //               color="error"
  //               onClick={() =>
  //                 roleStore.removePermission(params.row.id, perm.id)
  //               }
  //             >
  //               ➖
  //             </IconButton>
  //           </Box>
  //         ))}
  //       </Box>
  //     ),
  //   },
  //   {
  //     field: "actions",
  //     headerName: "Actions",
  //     flex: 1,
  //     renderCell: (params) => (
  //       <Box>
  //         <IconButton
  //           onClick={() => handleOpenModal(params.row.raw)}
  //           sx={{
  //             color: "#22C55E",
  //             mr: 1,
  //             "&:hover": { backgroundColor: "#BBF7D0" },
  //           }}
  //         >
  //           <EditIcon />
  //         </IconButton>
  //         <IconButton
  //           onClick={() => roleStore.deleteRole(params.row.id)}
  //           sx={{ color: "#6B7280", "&:hover": { backgroundColor: "#E5E7EB" } }}
  //         >
  //           <DeleteIcon />
  //         </IconButton>
  //       </Box>
  //     ),
  //   },
  // ];

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        flex: 0.6,
        minWidth: 100,
      },
      {
        field: "name",
        headerName: "Role Name",
        flex: 1.1,
        minWidth: 160,
      },
      {
        field: "description",
        headerName: "Description",
        flex: 1.6,
        minWidth: 240,
      },
      {
        field: "permissions",
        headerName: "Permissions",
        flex: 1.7,
        minWidth: 260,
        renderCell: (params) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {params.value?.map((perm) => (
              <Box key={perm.id} sx={{ display: "flex", alignItems: "center" }}>
                {perm.name}
                <IconButton
                  size="small"
                  color="error"
                  onClick={() =>
                    roleStore.removePermission(params.row.id, perm.id)
                  }
                  sx={{ ml: 0.5 }}
                >
                  ➖
                </IconButton>
              </Box>
            ))}
          </Box>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 160,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box>
            <IconButton
              onClick={() => handleOpenModal(params.row.raw)}
              sx={{
                color: "#22C55E",
                mr: 1,
                "&:hover": { backgroundColor: "#BBF7D0" },
              }}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              onClick={() => roleStore.deleteRole(params.row.id)}
              sx={{
                color: "#6B7280",
                "&:hover": { backgroundColor: "#E5E7EB" },
              }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ),
      },
    ],
    [handleOpenModal]
  );

  const rows = roleStore.roles.map((role) => ({
    id: role.id,
    name: role.name,
    description: role.description,
    permissions: roleStore.rolePermissions[role.id] || [],
    raw: role,
  }));

  return (
    <Box sx={{ p: 3 }}>
      <Card
        elevation={3}
        sx={{
          mb: 4,
          p: 2,
          background: "#F3F4F6",
          borderLeft: "4px solid #22C55E",
        }}
      >
        <CardContent>
          <Box display="flex" alignItems="center" mb={1}>
            <AdminPanelSettingsIcon
              fontSize="large"
              sx={{ mr: 2, color: "#22C55E" }}
            />
            <Typography variant="h4" fontWeight="bold">
              Roles Management
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage role-based permissions and more from this dashboard
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          onClick={() => handleOpenModal()}
          startIcon={<AddIcon />}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            backgroundColor: "#22C55E",
            color: "#FFFFFF",
            "&:hover": { backgroundColor: "#16A34A" },
          }}
        >
          Add Role
        </Button>
      </Box>

      <CustomDataGridPro
        rows={rows}
        columns={columns}
        isLoading={roleStore.isLoading}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalCount={rows.length}
      />

      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <DialogTitle>{editMode ? "Edit Role" : "Add New Role"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Role Name"
            fullWidth
            margin="normal"
            value={roleData.name}
            onChange={(e) => setRoleData({ ...roleData, name: e.target.value })}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            value={roleData.description}
            onChange={(e) =>
              setRoleData({ ...roleData, description: e.target.value })
            }
          />
          {editMode && (
            <>
              <Typography variant="h6" sx={{ mt: 2 }}>
                Assigned Permissions:
              </Typography>
              {roleStore.rolePermissions[currentRoleId]?.map((perm) => (
                <Box key={perm.id} display="flex" alignItems="center">
                  {perm.name}
                  <IconButton
                    color="error"
                    onClick={() =>
                      roleStore.removePermission(currentRoleId, perm.id)
                    }
                  >
                    ➖
                  </IconButton>
                </Box>
              ))}
              <Typography variant="h6" sx={{ mt: 2 }}>
                Assign New Permission:
              </Typography>
              <Select
                fullWidth
                value={selectedPermission}
                onChange={(e) => setSelectedPermission(e.target.value)}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select Permission
                </MenuItem>
                {roleStore.availablePermissions
                  .filter(
                    (perm) =>
                      !roleStore.rolePermissions[currentRoleId]?.some(
                        (p) => p.id === perm.id
                      )
                  )
                  .map((perm) => (
                    <MenuItem key={perm.id} value={perm.id}>
                      {perm.name}
                    </MenuItem>
                  ))}
              </Select>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#22C55E",
                  color: "#FFFFFF",
                  mt: 1,
                  "&:hover": { backgroundColor: "#16A34A" },
                }}
                disabled={!selectedPermission}
                onClick={() => {
                  roleStore.assignPermission(currentRoleId, selectedPermission);
                  setSelectedPermission("");
                }}
              >
                ➕ Add Permission
              </Button>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            sx={{
              textTransform: "uppercase",
              color: "#000",
              borderColor: "#000",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
                borderColor: "#000",
              },
            }}
            onClick={() => setIsModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            sx={{
              backgroundColor: "#22C55E",
              color: "#FFFFFF",
              "&:hover": { backgroundColor: "#16A34A" },
            }}
          >
            {editMode ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default RoleComponent;
